/**
 * <PalPicker> — custom button + listbox for choosing the raid boss.
 *
 * Replaces the <select> that used to live inside SpawnServerCard. A native
 * <option> CANNOT contain an <img> — browsers strip child markup — so a
 * select can only ever show a thumbnail for the already-selected pal, off to
 * the side. Per-row thumbnails require a custom listbox, and this one copies
 * PassivePicker's structure deliberately (trigger button, absolutely
 * positioned panel, search input, mousedown-to-close, capped result list) so
 * the admin panel has ONE interaction pattern, not two.
 *
 * HARD RULE — the value is never rewritten. `p.name` straight from
 * GET /admin/raids/pool is what gets posted to /admin/raids/spawn?pal=…, and
 * _resolve_asset_id / TOWER_BOSS_OVERRIDES match it exactly. Variant suffixes
 * ("Astralym (Gym)", "Bushi Noct (Predator)") and paired-trainer names
 * ("Zoe & Grizzbolt") are DISPLAY concerns only: the parenthetical is split
 * off into a chip for readability and the ampersand is left alone. Nothing
 * here reconstructs a name from its parts.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAnchoredPanel } from "@/components/admin/useAnchoredPanel";

// Rendered rows are capped because `others` is ~370 entries and painting
// every one with an image janks the panel open.
const MAX_ROWS = 120;

// The variant is already in the name as a trailing parenthetical — derived,
// never hardcoded, so a new suffix in paldata.json needs no code change.
const VARIANT_RE = /^(.*?)\s*\(([^)]+)\)\s*$/;

export function splitVariant(name) {
  const m = VARIANT_RE.exec(name || "");
  if (!m) return { base: name || "", variant: null };
  return { base: m[1], variant: m[2] };
}

/** Load one server's pal pool. Shared so both pages fetch identically. */
export function usePalPool(serverName) {
  const [state, setState] = useState({ pool: [], others: [] });
  useEffect(() => {
    if (!serverName) { setState({ pool: [], others: [] }); return undefined; }
    let cancelled = false;
    api.get(`/admin/raids/pool?server=${encodeURIComponent(serverName)}`)
      .then(({ data }) => {
        if (!cancelled) {
          setState({ pool: data?.pool || [], others: data?.others || [] });
        }
      })
      .catch(() => { /* leave empty — the picker still offers Random */ });
    return () => { cancelled = true; };
  }, [serverName]);
  return state;
}

function Thumb({ src, alt }) {
  // A missing OR dead image must not shift the row or show a broken-image
  // glyph. 70 of the 382 /pool rows point at a paldeck file that is not on
  // disk (69 of them variant-suffixed), so onError matters as much as a null
  // src — see the backend base-name fallback in _resolve_pal_image.
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className="w-5 h-5 rounded bg-white/5 border border-white/10 flex-shrink-0"
        aria-hidden="true" />
    );
  }
  return (
    <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)}
      className="w-5 h-5 rounded object-cover flex-shrink-0 bg-white/5" />
  );
}

function PalRow({ p, active, selected, onPick, onHover, slug }) {
  const { base, variant } = splitVariant(p.name);
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(p.name)}
        onMouseEnter={onHover}
        className={`w-full text-left flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5 ${
          active ? "bg-white/5" : ""
        }`}
        data-testid={`pal-option-${slug}-${p.name.replace(/\s+/g, "-")}`}
        data-pal-value={p.name}
      >
        <Thumb src={p.image} alt="" />
        <span className="text-[11px] flex-1 truncate text-[var(--bone)]">{base}</span>
        {variant && (
          <span className="text-[9px] px-1 rounded bg-sky-400/10 text-sky-300 border border-sky-400/20 flex-shrink-0">
            {variant}
          </span>
        )}
        {p.safe === false && (
          <span className="text-[9px] text-amber-400 flex-shrink-0" title="experimental">⚠</span>
        )}
        {selected && <Check size={11} className="text-[var(--teal)] flex-shrink-0" />}
      </button>
    </li>
  );
}

export function PalPicker({ value, onChange, pool = [], others = [], slug, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, panelRef, style } = useAnchoredPanel(open, close);

  // Raid pool keeps its stored order (legendaries above tower bosses);
  // only the search filters it.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hit = (p) => !q || p.name.toLowerCase().includes(q);
    const a = pool.filter(hit);
    const b = others.filter(hit).slice(0, Math.max(0, MAX_ROWS - a.length));
    return { poolRows: a.slice(0, MAX_ROWS), otherRows: b };
  }, [pool, others, query]);

  const flat = useMemo(
    () => [...groups.poolRows, ...groups.otherRows],
    [groups],
  );

  const pick = (name) => { onChange(name); setOpen(false); setQuery(""); };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (!flat.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(flat[Math.min(cursor, flat.length - 1)].name);
    }
  };

  const selected = [...pool, ...others].find((p) => p.name === value);
  const sel = splitVariant(selected?.name || "");

  const panel = (
    <div
      ref={panelRef}
      style={style || { display: "none" }}
      onKeyDown={onKeyDown}
      className="overflow-hidden rounded-md border border-white/10 bg-[#11131a] shadow-xl p-2 flex flex-col"
      data-testid={`admin-raids-pal-panel-${slug}`}
    >
      <input
        autoFocus
        value={query}
        onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
        placeholder={`Search ${pool.length + others.length} pals…`}
        className="input input-sm w-full text-[11px] mb-1.5 flex-shrink-0"
        data-testid={`admin-raids-pal-search-${slug}`}
      />

      <ul className="overflow-y-auto space-y-0.5 flex-1 min-h-0">
        <li>
          <button
            type="button"
            onClick={() => pick("")}
            className="w-full text-left flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5"
            data-testid={`pal-option-${slug}-random`}
            data-pal-value=""
          >
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">🎲</span>
            <span className="text-[11px] flex-1 text-[var(--muted)]">Random pal (safe pool)</span>
            {!value && <Check size={11} className="text-[var(--teal)]" />}
          </button>
        </li>

        {groups.poolRows.length > 0 && (
          <li className="text-[9px] uppercase tracking-wider text-[var(--muted)] px-1.5 pt-1.5">
            Raid pool
          </li>
        )}
        {groups.poolRows.map((p, i) => (
          <PalRow key={`pool-${p.name}`} p={p} slug={slug}
            active={i === cursor} selected={p.name === value}
            onPick={pick} onHover={() => setCursor(i)} />
        ))}

        {groups.otherRows.length > 0 && (
          <li className="text-[9px] uppercase tracking-wider text-[var(--muted)] px-1.5 pt-1.5">
            All pals ({others.length})
          </li>
        )}
        {groups.otherRows.map((p, i) => (
          <PalRow key={`other-${p.name}`} p={p} slug={slug}
            active={groups.poolRows.length + i === cursor}
            selected={p.name === value} onPick={pick}
            onHover={() => setCursor(groups.poolRows.length + i)} />
        ))}

        {flat.length === 0 && (
          <li className="text-[11px] text-[var(--muted)] px-1.5 py-2">No match.</li>
        )}
      </ul>
    </div>
  );

  return (
    <div className="relative flex-1 min-w-[140px]" onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        onClick={() => { setOpen((o) => !o); setQuery(""); setCursor(0); }}
        className="input text-xs w-full flex items-center gap-2 text-left disabled:opacity-50"
        data-testid={`admin-raids-pal-${slug}`}
        data-pal-value={value || ""}
      >
        {selected ? (
          <>
            <Thumb src={selected.image} alt="" />
            <span className="flex-1 truncate">{sel.base}</span>
            {sel.variant && (
              <span className="text-[9px] px-1 rounded bg-sky-400/10 text-sky-300 border border-sky-400/20">
                {sel.variant}
              </span>
            )}
            {selected.safe === false && <span className="text-[10px] text-amber-400">⚠</span>}
          </>
        ) : (
          <span className="flex-1 truncate text-[var(--muted)]">🎲 Random pal (safe pool)</span>
        )}
      </button>

      {/* Portalled to <body>: a .glass-card ancestor creates a stacking
          context, so an in-card panel is painted under the next card. */}
      {open && createPortal(panel, document.body)}
    </div>
  );
}
